import { lowBalanceWarnings, startWarningCycle } from './policy.js';
import { BILLING_POLICY_VERSION } from './schemas.js';
export const DEFAULT_METERING_LIMITS = {
    reserveWindowSeconds: 60,
    maxUnfundedExposureSeconds: 1800,
};
/** Deterministic ledger id for an event. `callKey` scopes reservation expiries. */
export function walletEventLedgerId(event, callKey) {
    switch (event.kind) {
        case 'allowance_grant':
            return ledgerIds.allowanceGrant(event.allowanceId);
        case 'allowance_expire':
            return event.reason === 'reservation_end'
                ? ledgerIds.allowanceReservationExpire(event.allowanceId, callKey ?? 'unknown')
                : ledgerIds.allowanceExpire(event.allowanceId);
        case 'allowance_reversal':
            return ledgerIds.allowanceReversal(event.providerId);
        case 'allowance_lapse':
            return null;
    }
}
/** Signed seconds an event moves (included credit only). */
export function walletEventDelta(event) {
    switch (event.kind) {
        case 'allowance_grant':
            return event.seconds;
        case 'allowance_expire':
        case 'allowance_reversal':
            return -event.seconds;
        case 'allowance_lapse':
            return 0;
    }
}
/**
 * The immutable ledger row for one event (without `createdAt`, which the
 * adapter stamps with its own server timestamp). Included-only by definition.
 */
export function walletEventLedgerEntry(businessId, event, balanceAfter, callKey = null) {
    const delta = walletEventDelta(event);
    return {
        kind: event.kind === 'allowance_lapse' ? 'allowance_expire' : event.kind,
        deltaSeconds: delta,
        includedDeltaSeconds: delta,
        purchasedDeltaSeconds: 0,
        allowanceId: event.allowanceId,
        businessId,
        billingModeVersion: BILLING_POLICY_VERSION,
        sourceType: event.kind === 'allowance_reversal' ? 'stripe_refund' : 'allowance',
        sourceId: event.kind === 'allowance_reversal' ? event.providerId : event.allowanceId,
        callKey: event.kind === 'allowance_expire' && event.reason === 'reservation_end' ? callKey : null,
        orderId: null,
        balanceAfter,
        reason: event.kind === 'allowance_expire' ? event.reason : '',
    };
}
/**
 * What an event changes on `billingAllowances/{id}`: a status (or none) and
 * counter increments. Shared so both adapters keep the record identical; each
 * translates `increments` into its SDK's `FieldValue.increment`.
 */
export function allowancePatchFor(event) {
    switch (event.kind) {
        case 'allowance_grant':
            return { allowanceId: event.allowanceId, status: 'active', increments: { grantedSeconds: event.seconds }, reversalId: null };
        case 'allowance_expire':
            return {
                allowanceId: event.allowanceId,
                status: event.reason === 'reservation_end' ? null : event.reason === 'superseded' ? 'superseded' : 'expired',
                increments: event.seconds > 0 ? { expiredSeconds: event.seconds } : {},
                reversalId: null,
            };
        case 'allowance_reversal':
            return { allowanceId: event.allowanceId, status: 'revoked', increments: event.seconds > 0 ? { reversedSeconds: event.seconds } : {}, reversalId: event.providerId };
        case 'allowance_lapse':
            return { allowanceId: event.allowanceId, status: 'expired', increments: {}, reversalId: null };
    }
}
// ── Source arithmetic ────────────────────────────────────────────────────────
const inc = (w) => w.includedSeconds ?? 0;
const incReserved = (w) => w.reservedIncludedSeconds ?? 0;
/** Is the wallet's allowance spendable at `now`? */
export function allowanceLive(w, now) {
    const a = w.allowance;
    if (!a || a.status !== 'active')
        return false;
    const t = now.getTime();
    return a.periodStart.getTime() <= t && t < a.periodEnd.getTime();
}
/** Purchased credit (signed; negative = debt). */
export function purchasedSeconds(w) {
    return w.balanceSeconds - (w.includedSeconds ?? 0);
}
/**
 * Seconds a NEW call or the auto top-up policy may count on at `now`:
 * posted balance minus reservations, minus included seconds whose period is
 * over but which no planner has expired yet. Debt is netted against the
 * aggregate (doc 59 N05: "net of reservations and debt").
 */
export function effectiveAvailableSeconds(s, now) {
    const included = s.includedSeconds ?? 0;
    const free = Math.max(0, included - (s.reservedIncludedSeconds ?? 0));
    const a = s.allowance ?? null;
    const live = !!a && a.status === 'active' && a.periodStart.getTime() <= now.getTime() && now.getTime() < a.periodEnd.getTime();
    const dead = live ? 0 : free;
    return Math.max(0, s.balanceSeconds - s.reservedSeconds - dead);
}
/**
 * Apply time to a wallet (doc 59 N03): end an allowance whose period is over
 * (its UNRESERVED seconds expire now; seconds reserved by open calls stay
 * until those calls settle, and may only pay for pre-boundary time), and
 * activate a paid period whose start has arrived. Pure and idempotent: a
 * wallet already rolled for `now` comes back unchanged with no events.
 */
export function rollWallet(w0, now) {
    let w = w0;
    const events = [];
    const t = now.getTime();
    if (w.allowance && w.allowance.status === 'active' && t >= w.allowance.periodEnd.getTime()) {
        const ended = endAllowance(w, 'period_end');
        w = ended.wallet;
        events.push(...ended.events);
    }
    const pending = w.pendingAllowance ?? null;
    if (pending && t >= pending.periodStart.getTime()) {
        if (t < pending.periodEnd.getTime()) {
            const activated = activateAllowance(w, pending);
            w = activated.wallet;
            events.push(...activated.events);
        }
        else {
            w = { ...w, pendingAllowance: null, version: w.version + 1 };
            events.push({ kind: 'allowance_lapse', allowanceId: pending.id });
        }
    }
    return { wallet: w, events };
}
/** Expire an allowance's unreserved seconds and mark it ended. */
function endAllowance(w, reason) {
    const a = w.allowance;
    const free = Math.max(0, inc(w) - incReserved(w));
    return {
        wallet: {
            ...w,
            includedSeconds: inc(w) - free,
            balanceSeconds: w.balanceSeconds - free,
            allowance: { ...a, status: 'ended' },
            version: w.version + 1,
        },
        events: [{ kind: 'allowance_expire', allowanceId: a.id, seconds: free, reason }],
    };
}
/** Grant a period's seconds and open a warning cycle on the new balance. */
function activateAllowance(w0, p) {
    let w = w0;
    const events = [];
    // Never two spendable allowances at once (A23): an overlapping older one is
    // superseded — its unused seconds expire, they are not carried over.
    if (w.allowance && w.allowance.status === 'active') {
        const ended = endAllowance(w, 'superseded');
        w = ended.wallet;
        events.push(...ended.events);
    }
    const balance = w.balanceSeconds + p.seconds;
    w = {
        ...w,
        balanceSeconds: balance,
        includedSeconds: inc(w) + p.seconds,
        allowance: { id: p.id, subscriptionId: p.subscriptionId, periodStart: p.periodStart, periodEnd: p.periodEnd, grantedSeconds: p.seconds, status: 'active' },
        pendingAllowance: w.pendingAllowance?.id === p.id ? null : (w.pendingAllowance ?? null),
        // A grant replenishes the balance: queued low-balance warnings of the old
        // cycle are suppressed at send time (A27). No receipt SMS is queued.
        ...startWarningCycle(balance, `cyc_allowance_${p.id}`),
        warningCycleSource: 'allowance',
        version: w.version + 1,
    };
    events.push({ kind: 'allowance_grant', allowanceId: p.id, seconds: p.seconds });
    return { wallet: w, events };
}
/**
 * Decide what one VERIFIED paid period means for the wallet at `now`
 * (doc 59 N02/N03, A01/A04/A09/A23). The caller has already proved the
 * invoice; this only places the period in time:
 *
 * - current period → grant now (an overlapping older allowance is superseded);
 * - future period → recorded as pending, not spendable before its start;
 * - period already over → historical, never spendable today;
 * - an OLDER period than the active one that still overlaps → review.
 */
export function planAllowanceGrant(wallet, grant, now) {
    const rolled = rollWallet(wallet, now);
    const w = rolled.wallet;
    const events = [...rolled.events];
    if (w.allowance?.id === grant.id || w.pendingAllowance?.id === grant.id)
        return { wallet: w, events, outcome: 'already' };
    const t = now.getTime();
    if (grant.periodEnd.getTime() <= t)
        return { wallet: w, events, outcome: 'historical' };
    if (grant.periodStart.getTime() > t) {
        // Two future periods at once is not a supported shape; the earlier one wins.
        if (w.pendingAllowance && w.pendingAllowance.periodStart.getTime() <= grant.periodStart.getTime()) {
            return { wallet: w, events, outcome: 'review_overlap' };
        }
        return { wallet: { ...w, pendingAllowance: grant, version: w.version + 1 }, events, outcome: 'scheduled' };
    }
    if (w.allowance && w.allowance.status === 'active' && w.allowance.periodStart.getTime() > grant.periodStart.getTime()) {
        return { wallet: w, events, outcome: 'review_overlap' };
    }
    const activated = activateAllowance(w, grant);
    return { wallet: activated.wallet, events: [...events, ...activated.events], outcome: 'granted' };
}
/**
 * Revoke one allowance after a FULL subscription refund (doc 59 N06): its
 * unused, unreserved seconds are removed once (the ledger id is the refund id);
 * seconds already used stay used; purchased credit is untouched. A pending
 * (future) period is simply dropped.
 */
export function planAllowanceRevocation(wallet, allowanceId, providerId, now) {
    const rolled = rollWallet(wallet, now);
    let w = rolled.wallet;
    const events = [...rolled.events];
    if (w.pendingAllowance?.id === allowanceId) {
        w = { ...w, pendingAllowance: null, version: w.version + 1 };
        return { wallet: w, events, removedSeconds: 0 };
    }
    if (w.allowance?.id !== allowanceId || w.allowance.status !== 'active')
        return { wallet: w, events, removedSeconds: 0 };
    const free = Math.max(0, inc(w) - incReserved(w));
    w = {
        ...w,
        includedSeconds: inc(w) - free,
        balanceSeconds: w.balanceSeconds - free,
        allowance: { ...w.allowance, status: 'ended' },
        version: w.version + 1,
    };
    events.push({ kind: 'allowance_reversal', allowanceId, seconds: free, providerId });
    return { wallet: w, events, removedSeconds: free };
}
/**
 * Admit one call (doc 42 §10.2 step 1, §10.3; doc 59 N04).
 *
 * New calls need AVAILABLE funds (included first, then purchased). On top of
 * that, the call's worst case — its fixed `maxCallSeconds`, added to every
 * other open call's remaining worst case — may exceed the posted balance by at
 * most the operator limit, so a burst of calls near zero cannot run up
 * unbounded debt.
 */
export function planAdmission(wallet, maxCallSeconds, limits, now) {
    const rolled = rollWallet(wallet, now);
    const w = rolled.wallet;
    const available = Math.max(0, w.balanceSeconds - w.reservedSeconds);
    if (available <= 0)
        return { ok: false, reason: 'no_funds' };
    const exposure = w.openExposureSeconds + maxCallSeconds;
    const unfunded = exposure - Math.max(0, w.balanceSeconds);
    if (unfunded > limits.maxUnfundedExposureSeconds)
        return { ok: false, reason: 'exposure_limit' };
    const reserveSeconds = Math.min(limits.reserveWindowSeconds, available, maxCallSeconds);
    const live = allowanceLive(w, now);
    const freeIncluded = live ? Math.max(0, inc(w) - incReserved(w)) : 0;
    const fromIncluded = Math.min(reserveSeconds, freeIncluded);
    return {
        ok: true,
        reserveSeconds,
        events: rolled.events,
        wallet: {
            ...w,
            reservedSeconds: w.reservedSeconds + reserveSeconds,
            reservedIncludedSeconds: incReserved(w) + fromIncluded,
            openExposureSeconds: exposure,
            version: w.version + 1,
        },
        usage: {
            state: 'reserved',
            maxCallSeconds,
            appliedMs: 0,
            chargedSeconds: 0,
            reservedSeconds: reserveSeconds,
            reservedIncludedSeconds: fromIncluded,
            includedAllowanceId: fromIncluded > 0 ? w.allowance.id : null,
            includedAllowanceStart: fromIncluded > 0 ? w.allowance.periodStart : null,
            includedAllowanceEnd: fromIncluded > 0 ? w.allowance.periodEnd : null,
            includedChargedSeconds: 0,
            purchasedChargedSeconds: 0,
        },
    };
}
/**
 * Apply a CUMULATIVE billable-time observation (doc 42 §10.2 steps 2–5;
 * doc 59 N04).
 *
 * - Monotonic: a late or replayed checkpoint carrying a smaller cumulative
 *   value charges nothing and reserves nothing.
 * - Seconds are `floor(totalMs / 1000)` of the whole call, never rounded per
 *   checkpoint or per call — 58 s costs 58 s; the sub-second tail goes the
 *   customer's way.
 * - Capped at the call's `maxCallSeconds`, fixed at admission.
 * - Every uncharged second is placed on the call's own timeline (second k
 *   starts at `billableStartedAt + k s`) and an allowance pays only for
 *   seconds inside its period — whenever the observation arrives. A second
 *   straddling a boundary belongs to the later period. So a delayed final
 *   observation never lets a renewed allowance pay for time before its start
 *   (doc 61 R01), and never the whole call goes to whichever period the
 *   final callback lands in (A12).
 * - Source order: this call's own included reservation, then unreserved
 *   included seconds of the live allowance (each within its period), then
 *   this call's purchased reservation, then unreserved purchased funds
 *   (possibly into debt). Another call's reservation is never taken (A10).
 * - Leftover included reservation from an ENDED period expires once the call
 *   has crossed the boundary (or ends); it is never released as spendable.
 * - With no known timeline start (a release before media) seconds cannot be
 *   placed: an ended period's reservation pays nothing more and the live
 *   allowance pays as if current — such observations never carry new time.
 * - `final` charges the unbilled tail and releases the reservation and the
 *   remaining exposure, atomically. A closed call never changes again.
 */
export function planCheckpoint(wallet, usage, cumulativeMs, final, limits, now, billableStartedAt = null) {
    const closedPlan = () => ({
        deltaSeconds: 0,
        includedDeltaSeconds: 0,
        purchasedDeltaSeconds: 0,
        chargedSeconds: usage.chargedSeconds,
        wallet,
        usage,
        events: [],
        includedUse: [],
        noop: true,
    });
    if (usage.state === 'closed')
        return closedPlan();
    const rolled = rollWallet(wallet, now);
    const w = rolled.wallet;
    const events = [...rolled.events];
    const capMs = usage.maxCallSeconds * 1000;
    const observed = Math.min(capMs, Math.max(0, Math.floor(cumulativeMs)));
    const appliedMs = Math.max(usage.appliedMs, observed);
    const charged = Math.floor(appliedMs / 1000);
    const prev = usage.chargedSeconds;
    const delta = Math.max(0, charged - prev);
    const live = allowanceLive(w, now);
    const liveId = live ? w.allowance.id : null;
    const ownId = usage.includedAllowanceId ?? null;
    const ownInc0 = usage.reservedIncludedSeconds ?? 0;
    const ownPurch0 = Math.max(0, usage.reservedSeconds - ownInc0);
    const ownIsLive = !!ownId && ownId === liveId;
    // Periods as half-open ranges of billable seconds on this call's timeline.
    const start = usage.billableStartedAt ?? billableStartedAt ?? null;
    const at = (d) => Math.floor((d.getTime() - start.getTime()) / 1000);
    const ownKnown = ownId && w.allowance?.id === ownId ? w.allowance : null;
    const ownStart = usage.includedAllowanceStart ?? ownKnown?.periodStart ?? null;
    const ownEnd = usage.includedAllowanceEnd ?? ownKnown?.periodEnd ?? null;
    let ownLo = Number.NEGATIVE_INFINITY;
    let ownHi = Number.POSITIVE_INFINITY;
    if (ownId && !ownIsLive) {
        // An ended period's reservation: only before its end, and only if that
        // end can be placed at all.
        ownHi = start && ownEnd ? at(ownEnd) : prev;
        if (start && ownStart)
            ownLo = at(ownStart);
    }
    else if (ownIsLive && start) {
        ownLo = at(w.allowance.periodStart);
        ownHi = at(w.allowance.periodEnd);
    }
    let liveLo = Number.NEGATIVE_INFINITY;
    let liveHi = Number.POSITIVE_INFINITY;
    if (live && start) {
        liveLo = at(w.allowance.periodStart);
        liveHi = at(w.allowance.periodEnd);
    }
    /** Uncharged seconds [prev, charged) inside [lo, hi). */
    const span = (lo, hi) => Math.max(0, Math.min(charged, hi) - Math.max(prev, lo));
    // The own reservation pays the earliest uncharged seconds of its period.
    const fromOwnIncluded = ownId ? Math.min(ownInc0, span(ownLo, ownHi)) : 0;
    const ownPaidLo = Math.max(prev, ownLo);
    const ownPaidHi = ownPaidLo + fromOwnIncluded;
    // The live allowance pays its period's seconds that the reservation did not.
    const liveSpan = live
        ? span(liveLo, liveHi) - Math.max(0, Math.min(ownPaidHi, liveHi) - Math.max(ownPaidLo, liveLo))
        : 0;
    const freeIncluded = live ? Math.max(0, inc(w) - incReserved(w)) : 0;
    const fromFreeIncluded = Math.min(delta - fromOwnIncluded, liveSpan, freeIncluded);
    const fromOwnPurchased = Math.min(delta - fromOwnIncluded - fromFreeIncluded, ownPurch0);
    const includedUsed = fromOwnIncluded + fromFreeIncluded;
    const purchasedUsed = delta - includedUsed;
    let ownInc = ownInc0 - fromOwnIncluded;
    let ownPurch = ownPurch0 - fromOwnPurchased;
    let included = inc(w) - includedUsed;
    let reservedIncluded = incReserved(w) - fromOwnIncluded;
    let reserved = w.reservedSeconds - fromOwnIncluded - fromOwnPurchased;
    let balance = w.balanceSeconds - delta;
    let exposure = Math.max(0, w.openExposureSeconds - delta);
    let allocId = ownInc > 0 ? ownId : null;
    let allocStart = ownInc > 0 ? ownStart : null;
    let allocEnd = ownInc > 0 ? (usage.includedAllowanceEnd ?? null) : null;
    // An ended period's reservation cannot pay for time after its boundary.
    if (ownInc > 0 && ownId && !ownIsLive && (final || charged >= ownHi)) {
        events.push({ kind: 'allowance_expire', allowanceId: ownId, seconds: ownInc, reason: 'reservation_end' });
        included -= ownInc;
        reservedIncluded -= ownInc;
        reserved -= ownInc;
        balance -= ownInc;
        ownInc = 0;
        allocId = null;
        allocStart = null;
        allocEnd = null;
    }
    let state = final ? 'closed' : 'active';
    if (final) {
        reserved -= ownInc + ownPurch;
        reservedIncluded -= ownInc;
        ownInc = 0;
        ownPurch = 0;
        allocId = null;
        allocStart = null;
        allocEnd = null;
        exposure = Math.max(0, exposure - (usage.maxCallSeconds - charged));
    }
    else {
        // Re-arm the window from what OTHER calls have not already reserved:
        // included seconds of the live allowance first, then purchased. Never
        // shrink a reservation here; settlement releases it.
        const remainingCall = usage.maxCallSeconds - charged;
        const othersReserved = reserved - ownInc - ownPurch;
        const cap = Math.max(0, Math.min(limits.reserveWindowSeconds, remainingCall, balance - othersReserved));
        const extra = cap - (ownInc + ownPurch);
        if (extra > 0) {
            // One usage record holds included seconds of ONE allowance only.
            const canTakeIncluded = live && (ownInc === 0 || allocId === liveId);
            const freeNow = canTakeIncluded ? Math.max(0, included - reservedIncluded) : 0;
            const addIncluded = Math.min(extra, freeNow);
            const addPurchased = extra - addIncluded;
            if (addIncluded > 0) {
                allocId = liveId;
                allocStart = w.allowance.periodStart;
                allocEnd = w.allowance.periodEnd;
            }
            ownInc += addIncluded;
            ownPurch += addPurchased;
            reservedIncluded += addIncluded;
            reserved += addIncluded + addPurchased;
        }
    }
    const includedUse = [];
    if (fromOwnIncluded > 0 && ownId)
        includedUse.push({ allowanceId: ownId, seconds: fromOwnIncluded });
    if (fromFreeIncluded > 0 && liveId) {
        const same = includedUse.find((u) => u.allowanceId === liveId);
        if (same)
            same.seconds += fromFreeIncluded;
        else
            includedUse.push({ allowanceId: liveId, seconds: fromFreeIncluded });
    }
    const nextUsage = {
        ...usage,
        state,
        appliedMs,
        chargedSeconds: charged,
        reservedSeconds: ownInc + ownPurch,
        reservedIncludedSeconds: ownInc,
        includedAllowanceId: allocId,
        includedAllowanceStart: allocStart,
        includedAllowanceEnd: allocEnd,
        includedChargedSeconds: (usage.includedChargedSeconds ?? 0) + includedUsed,
        purchasedChargedSeconds: (usage.purchasedChargedSeconds ?? 0) + purchasedUsed,
    };
    const noop = !final &&
        delta === 0 &&
        events.length === 0 &&
        appliedMs === usage.appliedMs &&
        nextUsage.reservedSeconds === usage.reservedSeconds &&
        (nextUsage.reservedIncludedSeconds ?? 0) === (usage.reservedIncludedSeconds ?? 0);
    if (noop)
        return closedPlan();
    return {
        deltaSeconds: delta,
        includedDeltaSeconds: includedUsed,
        purchasedDeltaSeconds: purchasedUsed,
        chargedSeconds: charged,
        wallet: {
            ...w,
            balanceSeconds: balance,
            includedSeconds: Math.max(0, included),
            reservedSeconds: Math.max(0, reserved),
            reservedIncludedSeconds: Math.max(0, reservedIncluded),
            openExposureSeconds: exposure,
            version: w.version + 1,
        },
        usage: nextUsage,
        events,
        includedUse,
        noop: false,
    };
}
/**
 * Credit PURCHASED seconds (doc 42 §8.3). A PAID top-up opens a new warning
 * cycle whose baseline is the balance after the credit; a migration grant may
 * open one too. A refund/chargeback is a negative credit and never opens a
 * cycle (§6 rule 10). Included seconds are never touched here.
 */
export function planCredit(wallet, deltaSeconds, opts) {
    const balance = wallet.balanceSeconds + deltaSeconds;
    const cycle = opts.openCycleId ? startWarningCycle(balance, opts.openCycleId) : null;
    return {
        ...wallet,
        balanceSeconds: balance,
        ...(cycle ?? {}),
        version: wallet.version + 1,
    };
}
/** Deterministic ledger ids — creating one IS the idempotency claim. */
export const ledgerIds = {
    purchase: (orderId) => `purchase:${orderId}`,
    usage: (callKey, chargedBoundary) => `usage:${callKey}:${chargedBoundary}`,
    migration: (version) => `migration:${version}`,
    refund: (refundId) => `refund:${refundId}`,
    chargeback: (disputeId) => `chargeback:${disputeId}`,
    allowanceGrant: (allowanceId) => `allowance_grant:${allowanceId}`,
    allowanceExpire: (allowanceId) => `allowance_expire:${allowanceId}`,
    allowanceReservationExpire: (allowanceId, callKey) => `allowance_expire:${allowanceId}:${callKey}`,
    allowanceReversal: (refundId) => `allowance_reversal:${refundId}`,
};
/** Outbox ids — one logical notification per cycle slot / per order. */
export const outboxIds = {
    lowBalance: (cycleId, slot) => `low:${cycleId}:${slot}`,
    topupSms: (orderId) => `topup:${orderId}`,
    autoEval: () => 'auto_eval',
    /** One "running out, auto top-up off" SMS per warning cycle. */
    autoOffSms: (cycleId) => `autooff:${cycleId}`,
    /** One "auto top-up failed" SMS per failed order, or per cycle for a cap/price/tax stop. */
    autoFailedSms: (key) => `autofail:${key}`,
    order: (orderId) => `order:${orderId}`,
};
/**
 * What a POSTED debit owes besides the debit itself (doc 42 §6, §9): warning
 * slots to claim (with at most one SMS job for the lowest), and whether the
 * auto top-up policy should be re-evaluated. Pure; the adapter writes the
 * outbox rows in the same transaction.
 */
export function postDebitEffects(wallet, prefs, auto, now = new Date()) {
    const decision = lowBalanceWarnings(prefs, wallet, wallet.balanceSeconds);
    let next = wallet;
    let lowJob = null;
    if (decision.send && wallet.warningCycleId) {
        next = { ...wallet, warnedSlots: [...wallet.warnedSlots, ...decision.claim] };
        // Keyed by the LOWEST slot claimed: one logical message per crossing.
        const lowest = decision.claim.includes('second') ? 'second' : 'first';
        lowJob = { id: outboxIds.lowBalance(wallet.warningCycleId, lowest), cycleId: wallet.warningCycleId, slots: decision.claim };
    }
    const available = effectiveAvailableSeconds(next, now);
    return { wallet: next, lowJob, autoEval: !!auto?.enabled && available <= auto.triggerSeconds };
}
/** The owner-readable projection written next to every wallet change. */
export function summaryFromState(s, extra) {
    const a = s.allowance ?? null;
    return {
        mode: s.mode,
        currency: s.currency,
        balanceSeconds: s.balanceSeconds,
        reservedSeconds: s.reservedSeconds,
        paymentState: s.paymentState,
        activeTopupOrderId: s.activeTopupOrderId,
        activeTopupExpiresAt: s.activeTopupOrderId ? (s.activeTopupExpiresAt ?? null) : null,
        includedSeconds: s.includedSeconds ?? 0,
        reservedIncludedSeconds: s.reservedIncludedSeconds ?? 0,
        offerVersion: s.offerVersion ?? null,
        allowanceStatus: a ? a.status : 'none',
        allowancePeriodStart: a?.periodStart ?? null,
        allowancePeriodEnd: a?.periodEnd ?? null,
        allowanceGrantedSeconds: a?.grantedSeconds ?? 0,
        pendingAllowanceStart: s.pendingAllowance?.periodStart ?? null,
        updatedAt: extra.updatedAt,
    };
}
/**
 * The owner-facing split of a wallet projection at `now` (doc 59 N07): what
 * is included (and until when), what was purchased, what calls hold, and what
 * a new call can use. Included seconds of a period that is over count as 0
 * even before any server write expired them.
 */
export function walletView(s, now) {
    const allowance = s.allowanceStatus !== 'none' && s.allowancePeriodStart && s.allowancePeriodEnd
        ? { status: s.allowanceStatus, periodStart: s.allowancePeriodStart, periodEnd: s.allowancePeriodEnd }
        : null;
    const live = !!allowance && allowance.status === 'active' && allowance.periodStart.getTime() <= now.getTime() && now.getTime() < allowance.periodEnd.getTime();
    const freeIncluded = Math.max(0, s.includedSeconds - s.reservedIncludedSeconds);
    return {
        includedRemainingSeconds: live ? freeIncluded : 0,
        purchasedSeconds: s.balanceSeconds - s.includedSeconds,
        reservedSeconds: s.reservedSeconds,
        availableSeconds: effectiveAvailableSeconds({ ...s, allowance }, now),
        allowanceLive: live,
        allowanceEndsAt: live ? allowance.periodEnd : null,
        pendingAllowanceStart: s.pendingAllowanceStart,
    };
}
/** A fresh outbox row. `createdAt`/`nextAttemptAt` are supplied by the caller
 * (a server timestamp in the adapters). */
export function newOutboxJob(businessId, kind, fields, at) {
    return {
        businessId,
        kind,
        state: 'pending',
        cycleId: fields.cycleId ?? null,
        slots: fields.slots ?? [],
        orderId: fields.orderId ?? null,
        smsReason: fields.smsReason ?? null,
        attempts: 0,
        nextAttemptAt: at,
        leaseToken: null,
        leaseUntil: null,
        maskedTo: null,
        language: null,
        body: null,
        segments: null,
        provider: null,
        providerMessageId: null,
        suppressReason: null,
        error: null,
        createdAt: at,
        updatedAt: at,
    };
}
//# sourceMappingURL=wallet.js.map